import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { editorApi, workloadApi } from '../lib/api';
import { Editor } from '../types';
import { useAuth, isAdminRole } from '../contexts/AuthContext';
import { Users, Plus, Edit2, Check, X, Trash2, Calendar, BarChart3 } from 'lucide-react';

function toDateStr(d: Date): string {
  return d.toISOString().split('T')[0];
}

export function EditorsPage() {
  const [editors, setEditors] = useState<Editor[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({ name: '', display_name: '' });
  const [error, setError] = useState('');
  const { user } = useAuth();

  // Date range + workload stats
  const [dateFrom, setDateFrom] = useState<string>(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return toDateStr(d);
  });
  const [dateTo, setDateTo] = useState<string>(toDateStr(new Date()));
  const [datePreset, setDatePreset] = useState<string>('30d');
  const [workloadMap, setWorkloadMap] = useState<Record<string, any>>({});

  const applyPreset = (preset: string) => {
    setDatePreset(preset);
    const now = new Date();
    setDateTo(toDateStr(now));
    if (preset === '7d') {
      const d = new Date(); d.setDate(d.getDate() - 7); setDateFrom(toDateStr(d));
    } else if (preset === '30d') {
      const d = new Date(); d.setDate(d.getDate() - 30); setDateFrom(toDateStr(d));
    } else if (preset === '90d') {
      const d = new Date(); d.setDate(d.getDate() - 90); setDateFrom(toDateStr(d));
    } else if (preset === 'all') {
      setDateFrom('');
    }
  };

  useEffect(() => {
    fetchEditors();
  }, []);

  useEffect(() => {
    fetchWorkloadStats();
  }, [dateFrom, dateTo]);

  const fetchEditors = async () => {
    try {
      const response = await editorApi.getAll(true); // Pass true to include stats
      setEditors(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch editors:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkloadStats = async () => {
    try {
      const params: { from?: string; to?: string } = {};
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      const response = await workloadApi.getOverview(params);
      const map: Record<string, any> = {};
      for (const e of response.data.data.editors) {
        map[e.id] = e;
      }
      setWorkloadMap(map);
    } catch (err) {
      console.error('Failed to fetch workload stats:', err);
    }
  };

  const handleAdd = async () => {
    if (!formData.name) {
      setError('Name is required');
      return;
    }

    try {
      await editorApi.create({
        name: formData.name,
        display_name: formData.display_name || formData.name,
      });
      setFormData({ name: '', display_name: '' });
      setShowAddForm(false);
      setError('');
      fetchEditors();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create editor');
    }
  };

  const handleUpdate = async (id: string) => {
    try {
      await editorApi.update(id, {
        display_name: formData.display_name,
      });
      setEditingId(null);
      setFormData({ name: '', display_name: '' });
      fetchEditors();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update editor');
    }
  };

  const startEdit = (editor: Editor) => {
    setEditingId(editor.id);
    setFormData({ name: editor.name, display_name: editor.display_name });
  };

  const handleDelete = async (id: string, displayName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${displayName}"? This will deactivate the editor.`)) {
      return;
    }

    try {
      await editorApi.delete(id);
      fetchEditors();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete editor');
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Editors">
        <div className="flex items-center justify-center h-96">
          <p className="text-muted-foreground">Loading editors...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Editors"
      actions={
        isAdminRole(user?.role) && (
          <Button onClick={() => setShowAddForm(!showAddForm)}>
            <Plus size={16} className="mr-2" />
            Add Editor
          </Button>
        )
      }
    >
      <div className="space-y-6">
        {/* Add Editor Form */}
        {showAddForm && (
          <Card>
            <CardHeader>
              <CardTitle>Add New Editor</CardTitle>
              <CardDescription>Create a new editor profile</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {error && (
                  <p className="text-sm text-destructive">{error}</p>
                )}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Name (uppercase, used in ad names)</label>
                  <Input
                    placeholder="e.g., JOHNSMITH"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase() })}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Display Name</label>
                  <Input
                    placeholder="e.g., John Smith"
                    value={formData.display_name}
                    onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleAdd}>Create Editor</Button>
                  <Button variant="outline" onClick={() => {
                    setShowAddForm(false);
                    setFormData({ name: '', display_name: '' });
                    setError('');
                  }}>
                    Cancel
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Date Range Filter */}
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-wrap gap-3 items-center">
              <Calendar size={18} className="text-muted-foreground" />
              <span className="text-sm font-medium">Workload Date Range:</span>

              <div className="flex gap-1">
                {[
                  { key: '7d', label: '7 days' },
                  { key: '30d', label: '30 days' },
                  { key: '90d', label: '90 days' },
                  { key: 'all', label: 'All Time' },
                ].map(p => (
                  <button
                    key={p.key}
                    onClick={() => applyPreset(p.key)}
                    className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                      datePreset === p.key
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background text-foreground border-border hover:bg-accent'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              <span className="text-muted-foreground text-sm">or</span>

              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setDatePreset('custom'); }}
                  className="px-3 py-1.5 text-sm border border-border rounded-md bg-background text-foreground"
                />
                <span className="text-sm text-muted-foreground">to</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setDatePreset('custom'); }}
                  className="px-3 py-1.5 text-sm border border-border rounded-md bg-background text-foreground"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Editors List */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {editors.map((editor) => {
            const wl = workloadMap[editor.id];
            return (
              <Card key={editor.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users size={24} className="text-primary" />
                      </div>
                      <div>
                        {editingId === editor.id ? (
                          <Input
                            value={formData.display_name}
                            onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                            className="h-8 text-sm"
                          />
                        ) : (
                          <>
                            <CardTitle className="text-lg">{editor.display_name}</CardTitle>
                            <p className="text-sm text-muted-foreground">{editor.name}</p>
                          </>
                        )}
                      </div>
                    </div>
                    {isAdminRole(user?.role) && (
                      <div className="flex gap-1">
                        {editingId === editor.id ? (
                          <>
                            <button
                              onClick={() => handleUpdate(editor.id)}
                              className="p-1 hover:bg-accent rounded"
                            >
                              <Check size={16} className="text-primary" />
                            </button>
                            <button
                              onClick={() => {
                                setEditingId(null);
                                setFormData({ name: '', display_name: '' });
                              }}
                              className="p-1 hover:bg-accent rounded"
                            >
                              <X size={16} className="text-muted-foreground" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => startEdit(editor)}
                              className="p-1 hover:bg-accent rounded"
                              title="Edit editor"
                            >
                              <Edit2 size={16} className="text-muted-foreground" />
                            </button>
                            <button
                              onClick={() => handleDelete(editor.id, editor.display_name)}
                              className="p-1 hover:bg-red-50 rounded"
                              title="Delete editor"
                            >
                              <Trash2 size={16} className="text-red-600" />
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    {/* Workload stats from date range */}
                    {wl && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Assigned Creatives:</span>
                          <span className="font-semibold text-purple-600">{wl.totalAssignedCreatives ?? 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Completed Creatives:</span>
                          <span className="font-semibold text-green-600">{wl.totalCompletedCreatives ?? wl.totalUploadedCreatives ?? 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Completion Rate:</span>
                          <span className={`font-semibold ${
                            (wl.completionRate ?? 0) >= 80 ? 'text-green-600' :
                            (wl.completionRate ?? 0) >= 50 ? 'text-yellow-600' : 'text-muted-foreground'
                          }`}>{wl.completionRate ?? 0}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Active Requests:</span>
                          <span className="font-medium">{wl.activeRequests ?? 0}</span>
                        </div>
                        {/* Progress bar */}
                        {(wl.totalAssignedCreatives ?? 0) > 0 && (
                          <div className="pt-1">
                            <div className="w-full bg-muted rounded-full h-1.5">
                              <div
                                className="h-1.5 rounded-full transition-all"
                                style={{
                                  width: `${Math.min(wl.completionRate ?? 0, 100)}%`,
                                  backgroundColor: (wl.completionRate ?? 0) >= 80 ? '#10b981' :
                                    (wl.completionRate ?? 0) >= 50 ? '#f59e0b' : '#6b7280'
                                }}
                              />
                            </div>
                          </div>
                        )}
                        <div className="border-t border-border my-1 pt-1" />
                      </>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Media Files:</span>
                      <span className="font-medium">{editor.media_file_count || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Ads:</span>
                      <span className="font-medium">{editor.ad_count || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total Spend:</span>
                      <span className="font-medium">${Math.round(editor.total_spend || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Status:</span>
                      <span className={editor.is_active ? 'text-primary' : 'text-muted-foreground'}>
                        {editor.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {editors.length === 0 && (
          <Card className="p-12 text-center">
            <p className="text-muted-foreground">No editors found</p>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
